import React, { useState } from 'react'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../ui/card'
import { Button } from '../ui/button'
import { Input } from '../ui/input'
import { Label } from '../ui/label'
import { Building2, User, Mail, Phone, AlertCircle } from 'lucide-react'
import { companyAPI } from '../../services/api'

export default function CompanyIntakeForm({ onComplete }) {
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState('')
  const [formData, setFormData] = useState({
    companyName: '',
    pocName: '',
    pocEmail: '',
    pocPhone: ''
  })

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    })
    setError('')
  }

  const handleSubmit = async (e) => {
    e.preventDefault()
    setError('')

    // Validate all fields
    if (!formData.companyName || !formData.pocName || !formData.pocEmail || !formData.pocPhone) {
      setError('All fields are required')
      return
    }

    // Validate email format
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
    if (!emailRegex.test(formData.pocEmail)) {
      setError('Please enter a valid email address')
      return
    }

    // Validate phone format (basic validation)
    const phoneRegex = /^[\d\s\-\+\(\)]+$/
    if (!phoneRegex.test(formData.pocPhone) || formData.pocPhone.length < 10) {
      setError('Please enter a valid phone number')
      return
    }

    setLoading(true)
    try {
      const response = await companyAPI.completeIntake(formData)
      if (response.data.success) {
        onComplete(response.data.company)
      }
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to submit intake form')
    } finally {
      setLoading(false)
    }
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-green-50 to-green-100 flex items-center justify-center p-4">
      <Card className="w-full max-w-2xl">
        <CardHeader className="text-center">
          <div className="mx-auto mb-4 h-16 w-16 rounded-full bg-green-100 flex items-center justify-center">
            <Building2 className="h-8 w-8 text-green-600" />
          </div>
          <CardTitle className="text-2xl font-bold">Welcome to IFTA 2025</CardTitle>
          <CardDescription className="text-base mt-2">
            Please complete the company intake form to get started. This information is required to access the platform.
          </CardDescription>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleSubmit} className="space-y-6">
            {error && (
              <div className="p-3 bg-red-50 border border-red-200 rounded-lg flex items-center gap-2 text-red-700">
                <AlertCircle className="h-4 w-4 flex-shrink-0" />
                <span className="text-sm">{error}</span>
              </div>
            )}

            <div className="space-y-4">
              <div>
                <Label htmlFor="companyName" className="flex items-center gap-2 mb-2">
                  <Building2 className="h-4 w-4 text-gray-500" />
                  Company Name *
                </Label>
                <Input
                  id="companyName"
                  name="companyName"
                  type="text"
                  placeholder="Enter your company name"
                  value={formData.companyName}
                  onChange={handleChange}
                  autoComplete="off"
                  disabled={loading}
                  className="w-full"
                />
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="pocName" className="flex items-center gap-2 mb-2">
                    <User className="h-4 w-4 text-gray-500" />
                    Point of Contact Name *
                  </Label>
                  <Input
                    id="pocName"
                    name="pocName"
                    type="text"
                    placeholder="Full name"
                    value={formData.pocName}
                    onChange={handleChange}
                    autoComplete="off"
                    disabled={loading}
                  />
                </div>

                <div>
                  <Label htmlFor="pocPhone" className="flex items-center gap-2 mb-2">
                    <Phone className="h-4 w-4 text-gray-500" />
                    POC Phone Number *
                  </Label>
                  <Input
                    id="pocPhone"
                    name="pocPhone"
                    type="tel"
                    placeholder="+1 (555) 123-4567"
                    value={formData.pocPhone}
                    onChange={handleChange}
                    autoComplete="off"
                    disabled={loading}
                  />
                </div>
              </div>

              <div>
                <Label htmlFor="pocEmail" className="flex items-center gap-2 mb-2">
                  <Mail className="h-4 w-4 text-gray-500" />
                  POC Email Address *
                </Label>
                <Input
                  id="pocEmail"
                  name="pocEmail"
                  type="email"
                  placeholder="poc@company.com"
                  value={formData.pocEmail}
                  onChange={handleChange}
                  autoComplete="off"
                  disabled={loading}
                  className="w-full"
                />
                <p className="text-xs text-gray-500 mt-1">
                  This email will be used for all platform communications
                </p>
              </div>
            </div>

            <div className="pt-4">
              <Button
                type="submit"
                className="w-full bg-green-600 hover:bg-green-700"
                disabled={loading}
              >
                {loading ? 'Submitting...' : 'Complete Registration'}
              </Button>
            </div>

            <div className="text-center text-sm text-gray-500">
              <p>By submitting this form, you agree to provide accurate information.</p>
              <p className="mt-1">All fields marked with * are required.</p>
            </div>
          </form>
        </CardContent>
      </Card>
    </div>
  )
}